import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import {
  HANDOVER_STATUS_CREATED,
  HANDOVER_STATUS_REJECTED,
  HANDOVER_STATUS_TAKEN,
  HANDOVER_STATUS_TAKE_PENDING,
  HANDOVER_TYPE_HO2_ID,
  HANDOVER_TYPE_HO3_ID,
  HANDOVER_TYPE_HO4_ID,
  HANDOVER_TYPE_HO5_ID,
  HANDOVER_TYPE_HOEC_ID,
  PAE_SUPPORT_EMAIL,
  PERMISSION_CLAIM_HO,
  PERMISSION_ENGINEERING_COMPLETION,
  PERMISSION_RELEASE_HO_ID,
} from 'src/app/handover-constants';
import { Handover } from 'src/app/interfaces/handover';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { HoClaimMessages, HoReleaseMessages } from '../home-messages';

@Component({
  selector: 'app-handover-card',
  templateUrl: './handover-card.component.html',
  styleUrls: ['./handover-card.component.scss'],
  standalone: false,
})
export class HandoverCardComponent implements OnInit {
  @Input() handover: Handover | undefined;
  @Output() reload = new EventEmitter<boolean>();
  @Input() destinationLabel: string | undefined;

  isSupervisor = false;
  canRelease = false;
  canClaim = false;
  HANDOVER_STATUS_TAKE_PENDING = HANDOVER_STATUS_TAKE_PENDING;
  HANDOVER_STATUS_TAKEN = HANDOVER_STATUS_TAKEN;
  HANDOVER_STATUS_REJECTED = HANDOVER_STATUS_REJECTED;
  HANDOVER_STATUS_CREATED = HANDOVER_STATUS_CREATED;
  destinationText: string | undefined;
  isHo3 = false;
  isEngineeringUser = false;
  userBelongAreaFrom = false;
  isSendingUserLogged = false;
  isSendingEngineerUserLogged = false;
  isReceiverUserLogged = false;
  isReceiverEngineerUserLogged = false;
  isDr = false;

  constructor(
    private router: Router,
    public dialogService: DialogService,
    private handoverService: HandoverService,
    private notificationService: NotificationService,
    private userService: UserService,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry
  ) {
    this.matIconRegistry.addSvgIcon(
      'share-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/share-alt.svg'
      )
    );
  }

  ngOnInit(): void {
    this.isSendingUserLogged =
      this.userService.getUserProperties().userId ==
      this.handover?.sendingUserId;
    this.isSendingEngineerUserLogged =
      this.userService.getUserProperties().userId ==
      this.handover?.sendingEngineerUserId;
    this.isReceiverUserLogged =
      this.userService.getUserProperties().userId == this.handover?.receiverId;
    this.isReceiverEngineerUserLogged =
      this.userService.getUserProperties().userId ==
      this.handover?.receivingEngineerUserId;
    this.isEngineeringUser = this.userService.hasPermission(
      PERMISSION_ENGINEERING_COMPLETION
    );
    this.isHo3 = this.handover?.handoverTypeId == HANDOVER_TYPE_HO3_ID;
    this.userBelongAreaFrom = this.userService.hasOperationalAreaAndPermission(
      this.handover!.fromAreaId!,
      PERMISSION_CLAIM_HO
    );

    this.canRelease = this.canReleaseHO();

    this.canClaim = this.canClaimHO();

    this.destinationText = this.handover?.fromAreaName;
    if (
      this.destinationLabel == 'Hacia' &&
      (this.handover?.handoverStatusId == HANDOVER_STATUS_TAKEN ||
        this.handover?.handoverStatusId == HANDOVER_STATUS_TAKE_PENDING ||
        (this.isHo3 &&
          this.handover?.handoverStatusId == HANDOVER_STATUS_CREATED))
    ) {
      this.destinationText = this.handover?.toAreaName;
    }

    this.isDr = [
      HANDOVER_TYPE_HO2_ID,
      HANDOVER_TYPE_HO4_ID,
      HANDOVER_TYPE_HO5_ID,
      HANDOVER_TYPE_HOEC_ID,
    ].includes(this.handover!.handoverTypeId!);
  }

  gotoHandover(): void {
    this.router.navigate(['/handover', this.handover?.handoverId], {
      state: { backTo: 'home' },
    });
  }

  release(): void {
    let takenByUser = '';
    if (this.handover?.handoverStatusId == HANDOVER_STATUS_TAKEN) {
      takenByUser = this.handover.receivingUser!;
    }
    if (
      this.handover?.handoverStatusId == HANDOVER_STATUS_REJECTED ||
      this.handover?.handoverStatusId == HANDOVER_STATUS_CREATED
    ) {
      takenByUser = !this.isEngineeringUser
        ? this.handover.sendingUser!
        : this.handover.sendingEngineerUser!;
    }
    const dialogRef = this.dialogService.openConfirmDialog(
      'Liberar Handover',
      `¿Estás seguro que querés <b>liberar el Handover del Pozo ${this.handover?.wellName}</b> del <b>Yacimiento ${this.handover?.wellProject} </b>
      tomado por <b>${takenByUser}</b> y que vuelva a la bandeja general?`
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.handoverService
          .releaseHandover(
            this.handover?.handoverId ? this.handover.handoverId : 0,
            this.handover?.handoverTypeId ? this.handover.handoverTypeId : 0,
            this.isEngineeringUser
          )
          .subscribe((handoverReleaseResult) => {
            if (handoverReleaseResult.handover) {
              let message = `${HoReleaseMessages.RELEASE_HO_SUCCESS_INI} ${handoverReleaseResult.handover.handoverId} ${HoReleaseMessages.RELEASE_HO_SUCCESS_END}`;
              if (!handoverReleaseResult.notificationSent) {
                message += `\nLa acción fue realizada, pero no se ha podido notificar vía mail al usuario que lo tenía tomado.\nPor favor, notifique a ${PAE_SUPPORT_EMAIL} de este inconveniente.`;
              }
              this.notificationService.notify(
                message,
                HoReleaseMessages.SUCCESS_NOTIFICATION
              );
              this.reload.emit(true);
            } else {
              this.notificationService.notify(
                `${HoReleaseMessages.RELEASE_HO_ERROR_INI} \n${HoReleaseMessages.RELEASE_HO_ERROR_END}`,
                HoReleaseMessages.ERROR_NOTIFICATION
              );
            }
          });
      }
    });
  }

  claim(): void {
    let dialogTitle = 'Tomar Handover';
    let dialogContent = `¿Estás seguro que querés <b>tomar el Handover</b> del <b>Pozo ${this.handover?.wellName}</b> del <b>Yacimiento ${this.handover?.wellProject}</b>?`;
    if (this.isHo3) {
      dialogTitle = 'Completar Handover';
      dialogContent = `¿Estás seguro que querés <b>completar el Handover</b> del <b>Pozo ${this.handover?.wellName}</b> del <b>Yacimiento ${this.handover?.wellProject}</b>?`;
    }
    this.dialogService
      .openConfirmDialog(dialogTitle, dialogContent)
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.handoverService
            .claimHandover(
              this.handover?.handoverId ? this.handover.handoverId : 0,
              this.handover?.handoverTypeId ? this.handover.handoverTypeId : 0,
              this.isEngineeringUser
            )
            .subscribe((handover) => {
              if (handover) {
                this.notificationService.notify(
                  `${HoClaimMessages.CLAIM_HO_SUCCESS_INI} ${handover.handoverId} ${HoClaimMessages.CLAIM_HO_SUCCESS_END}`,
                  HoClaimMessages.SUCCESS_NOTIFICATION
                );
                this.reload.emit(true);
              } else {
                this.notificationService.notify(
                  `${HoClaimMessages.CLAIM_HO_ERROR_INI} \n${HoClaimMessages.CLAIM_HO_ERROR_END}`,
                  HoClaimMessages.ERROR_NOTIFICATION
                );
              }
            });
        }
      });
  }

  canReleaseHO(): boolean {
    // el user tiene que tener permiso para Liberar, ug, area op y distrito asociados(md_user_relations_link)
    // el HO tiene UG, area op y distrito.
    let canReleaseHandover = false;
    const ugHO = this.handover?.wellManagementUnitId;
    const opAreaHO = this.handover?.wellOperationalAreaId;
    const districtHO = this.handover?.wellDistrictId;
    let hasDistrict = false;
    let hasOperationalArea = false;
    let hasManagementUnit = false;
    let hasPermission = false;
    const roles = this.userService.getUserProperties().roles;

    if (roles) {
      for (let index = 0; index < roles.length; index++) {
        const rol = roles[index];
        // Permission
        const permissions = rol.permissions;
        if (permissions) {
          hasPermission =
            permissions.find(
              (x) => x.permissionId == PERMISSION_RELEASE_HO_ID
            ) != null;
        }

        // Area Operacional
        const operationalArea = rol.operationalArea;
        if (operationalArea) {
          hasOperationalArea = operationalArea.operationalAreaId == opAreaHO;
        }
        const district = rol.district?.districtId;

        const canReleaseAux =
          !this.isHo3 ||
          !this.isEngineeringUser ||
          this.handover?.sendingEngineerUser != null;

        // tiene distrito
        if (district) {
          hasDistrict = district == districtHO;
          canReleaseHandover =
            hasDistrict && hasOperationalArea && hasPermission && canReleaseAux;
        } else {
          const managementUnit = rol.managementUnit;
          if (managementUnit) {
            hasManagementUnit = managementUnit.managementUnitId == ugHO;
            canReleaseHandover =
              hasManagementUnit &&
              hasOperationalArea &&
              hasPermission &&
              canReleaseAux;
          }
        }
        if (canReleaseHandover) {
          break;
        }
      }
    }
    return canReleaseHandover;
  }

  canClaimHO(): boolean {
    let canClaimHandover = false;
    const roles = this.userService.getUserProperties().roles;

    if (roles) {
      for (let index = 0; index < roles.length; index++) {
        const rol = roles[index];
        // Permission
        const permissions = rol.permissions;
        if (permissions) {
          canClaimHandover =
            permissions.find((x) => x.permissionId == PERMISSION_CLAIM_HO) !=
            null;
        }
        if (canClaimHandover) {
          break;
        }
      }
    }
    return canClaimHandover;
  }

  getInitialsOfName(fullName: string | undefined): string {
    let initialsName = '';

    if (fullName) {
      if (fullName == 'mí') {
        const name = this.userService.getUserProperties().firstname;
        const lastName = this.userService.getUserProperties().lastname;
        const nameInitial = name ? name.charAt(0).toUpperCase() : '';
        const lastNameInitial = lastName
          ? lastName.charAt(0).toUpperCase()
          : '';
        initialsName = nameInitial + lastNameInitial;
        initialsName = initialsName ? initialsName : fullName.toUpperCase();
      } else {
        const fullNameList: string[] = fullName.split(' ');
        // Take initials for the fist name and the lastname.
        initialsName =
          fullNameList[0].charAt(0).toUpperCase() +
          fullNameList[fullNameList.length - 1].charAt(0).toUpperCase();
      }
    } else {
      initialsName = 'N/N';
    }
    return initialsName;
  }
}
